/**
 * Context Builder Utility
 * 
 * Builds refinement context from form data for AI requirements generation
 * Includes Step 0 prompt, Step 1 data, current requirements, and refinement history
 */

import { JobPostFormData } from '../../../schemas/jobPostValidation';
import { Requirements } from '../../../../../Entities/JobDescription';

export interface RefinementContext {
  initialPrompt?: string;
  step1Data?: {
    title?: string;
    position?: string;
    description?: string;
    numberOfOpenings?: number;
    ctc?: string;
    currencyCode?: string;
  };
  currentRequirements?: Array<{
    id?: number;
    topic?: string;
    description: string;
    mustMatch: boolean;
  }>;
  refinementHistory?: Array<{
    prompt: string;
    timestamp: string;
    requirementsBeforeCount?: number;
    requirementsAfterCount?: number;
    action?: string;
  }>;
}

/**
 * Build refinement context from form data
 * 
 * @param formData - Complete form data from React Hook Form
 * @returns RefinementContext object ready to send to backend
 */
export const buildRefinementContext = (
  formData: JobPostFormData
): RefinementContext => {
  const context: RefinementContext = {};

  // Extract initial prompt from Step 0
  if (formData.step0?.prompt?.trim()) {
    context.initialPrompt = formData.step0.prompt.trim();
  }

  // Extract Step 1 data (job details)
  if (formData.step1) {
    context.step1Data = {
      title: formData.step1.title || undefined,
      position: formData.step1.position || undefined,
      description: formData.step1.description || undefined,
      numberOfOpenings: formData.step1.numberOfOpenings || undefined,
      ctc: formData.step1.ctc?.toString() || undefined,
      currencyCode: formData.step1.currencyCode || undefined,
    };

    // Remove undefined values
    Object.keys(context.step1Data).forEach((key) => {
      if (context.step1Data![key as keyof typeof context.step1Data] === undefined) {
        delete context.step1Data![key as keyof typeof context.step1Data];
      }
    });

    // If step1Data is empty, set to undefined
    if (Object.keys(context.step1Data).length === 0) {
      context.step1Data = undefined;
    }
  }

  // Extract current requirements from Step 2
  if (formData.step2?.requirements && formData.step2.requirements.length > 0) {
    context.currentRequirements = formData.step2.requirements.map((req) => ({
      id: req.id,
      topic: req.topic || undefined,
      description: req.description,
      mustMatch: req.mustMatch,
    }));
  }

  // Extract refinement history from Step 2 (include ALL history for complete context)
  // Include initial requirements state if available in history
  if (formData.step2?.history && formData.step2.history.length > 0) {
    // Include all history entries, but prioritize recent ones
    // Send last 10 entries to balance context vs token limits
    const historyEntries = formData.step2.history.slice(-10);
    context.refinementHistory = historyEntries.map((entry) => ({
      prompt: entry.prompt,
      timestamp: new Date(entry.timestamp).toISOString(),
      // Include requirements before/after for better context
      requirementsBeforeCount: entry.requirementsBefore?.length || 0,
      requirementsAfterCount: entry.requirementsAfter?.length || 0,
      action: entry.action,
    }));
  }

  return context;
};

